import React from 'react';
import { ScheduleEntry, ScheduleErrorState, TruckLocation } from '../types';
import { formatDayOfWeek, formatDate, formatTime12Hour } from '../utils';
import './ScheduleWidget.css';

interface ScheduleWidgetProps {
  scheduleData: ScheduleEntry[];
  truckId: string;
  selectedTruck?: TruckLocation;
  environment: string;
  hasMultipleLocations?: boolean;
  onBackToLocations?: () => void;
  scheduleError?: ScheduleErrorState;
  themeStyles?: Record<string, string>;
}

const ScheduleWidget: React.FC<ScheduleWidgetProps> = ({
  scheduleData,
  truckId,
  selectedTruck,
  environment,
  hasMultipleLocations = false,
  onBackToLocations,
  scheduleError,
  themeStyles
}) => {
  // Group schedule entries by date to handle multiple time slots per day
  const groupedScheduleData = scheduleData.reduce((acc, entry) => {
    const date = entry.date;
    if (!acc[date]) {
      acc[date] = [];
    }
    acc[date].push(entry);
    return acc;
  }, {} as Record<string, ScheduleEntry[]>);

  // Convert grouped data back to array format, maintaining order
  const dailyScheduleData = Object.keys(groupedScheduleData).map(date => {
    const dayEntries = groupedScheduleData[date];
    const firstEntry = dayEntries[0];
    
    return {
      date,
      dayOfWeek: firstEntry.dayOfWeek,
      entries: dayEntries.sort((a, b) => {
        // Sort by opening time if both have times, otherwise put closed entries last
        if (!a.isOpen && !b.isOpen) return 0;
        if (!a.isOpen) return 1;
        if (!b.isOpen) return -1;
        if (!a.openTime || !b.openTime) return 0;
        return a.openTime.localeCompare(b.openTime);
      }),
      hasMultipleSlots: dayEntries.length > 1,
      isAnyOpen: dayEntries.some(entry => entry.isOpen)
    };
  });
  // If there's a schedule error, show error message with retry button
  if (scheduleError?.hasError) {
    return (
      <div 
        className="schedule-widget"
        style={themeStyles || {}}
      >
        <div className="schedule-header">
          {hasMultipleLocations && onBackToLocations && (
            <button 
              className="back-button"
              onClick={onBackToLocations}
              aria-label="Back"
            >
              <svg 
                width="20" 
                height="20" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2"
              >
                <path d="M19 12H5M12 19l-7-7 7-7" />
              </svg>
              Back
            </button>
          )}
          <h2 className={`schedule-title ${hasMultipleLocations ? 'with-back-button' : ''}`}>Opening Hours</h2>
          {selectedTruck && (
            <div className="truck-info">
              <div className="truck-name">{selectedTruck.name}</div>
              <div className="truck-address">{selectedTruck.defaultLocation.formatted_address}</div>
            </div>
          )}
        </div>
        
        <div className="schedule-error">
          <div className="error-icon">
            <svg 
              width="48" 
              height="48" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor" 
              strokeWidth="2"
            >
              <circle cx="12" cy="12" r="10"/>
              <line x1="12" y1="8" x2="12" y2="12"/>
              <line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
          </div>
          <h3 className="error-title">{scheduleError.errorMessage}</h3>
          <p className="error-description">We couldn't load the schedule information. Please check your connection and try again.</p>
          <button 
            className="retry-button" 
            onClick={scheduleError.onRetry}
            aria-label="Try again to load schedule"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="schedule-widget"
      style={themeStyles || {}}
    >
      <div className="schedule-header">
        {hasMultipleLocations && onBackToLocations && (
          <button 
            className="back-button"
            onClick={onBackToLocations}
            aria-label="Back"
          >
            <svg 
              width="20" 
              height="20" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor" 
              strokeWidth="2"
            >
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
            Back
          </button>
        )}
        <h2 className={`schedule-title ${hasMultipleLocations ? 'with-back-button' : ''}`}>Opening Hours</h2>
        {selectedTruck && (
          <div className="truck-info">
            <div className="truck-name">{selectedTruck.name}</div>
            <div className="truck-address">{selectedTruck.defaultLocation.formatted_address}</div>
          </div>
        )}
      </div>
      
      <div className="schedule-list">
        {dailyScheduleData.map((dayData, dayIndex) => (
          <div 
            key={dayData.date} 
            className={`schedule-item ${dayIndex === 0 ? 'today' : ''} ${!dayData.isAnyOpen ? 'closed' : ''}`}
          >
            <div className="day-info">
              <div className="day-name">
                {formatDayOfWeek(dayData.date)}
                {dayIndex === 0 && <span className="today-label">Today</span>}
              </div>
              <div className="day-date">{formatDate(dayData.date)}</div>
            </div>
            
            <div className="hours-info">
              {!dayData.isAnyOpen ? (
                <span className="closed-text">Closed</span>
              ) : (
                <div className="time-slots">
                  {dayData.entries
                    .filter(entry => entry.isOpen)
                    .map((entry, entryIndex) => (
                      <div key={entryIndex} className="time-slot">
                        <div className="time-slot-row">
                          <span className="hours-text">
                            {formatTime12Hour(entry.openTime)} - {formatTime12Hour(entry.closeTime)}
                          </span>
                          {entry.isPrivate && (
                            <span className="status-pill private-event">Private Event</span>
                          )}
                        </div>
                        {entry.notes && (
                          <div className="notes-text">{entry.notes}</div>
                        )}
                      </div>
                    ))
                  }
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
      
      <div className="schedule-footer">
      </div>
    </div>
  );
};

export default ScheduleWidget;
